<template>
  <SidebarDrawer
    v-model="mobile_open"
    :expand_storage_key="storage_key_expand"
    :width_expanded="260"
    :width_collapsed="60"
    data_test="navbar-employee-sidebar"
  >
    <template #client_switcher="{ is_expanded }">
      <SidebarClientSwitcher
        :client="current_client"
        :is_expanded="is_expanded"
        avatar_format="square"
        data_test="employee-sidebar-switch-client"
      >
        <DropdownOption
          v-if="has_multiple_profiles"
          data-test="sidebar-switch-company"
          @click="openSelectTenantModal"
        >
          <AppIcon
            name="regular-building"
            fixed-width
            size="sm"
          />
          <span>{{ $t('navbar.topnavbar.selectTeam') }}</span>
        </DropdownOption>

        <AppLink
          hide_underline
          :to="{ name: ROUTES_EMPLOYEES.CLIENT_COMPANY }"
        >
          <DropdownOption data-test="sidebar-settings">
            <AppIcon
              name="regular-gear"
              fixed-width
              size="sm"
            />
            <span>{{ $t('navbar.topnavbar.settings') }}</span>
          </DropdownOption>
        </AppLink>

        <AppLink
          hide_underline
          :to="{ name: ROUTES_EMPLOYEES.SETTINGS_TEAM }"
        >
          <DropdownOption data-test="sidebar-invite-manage-members">
            <AppIcon
              name="regular-user-group"
              fixed-width
              size="sm"
            />
            <span>{{ $t('navbar.topnavbar.inviteManageMembers') }}</span>
          </DropdownOption>
        </AppLink>
      </SidebarClientSwitcher>
    </template>

    <template #nav="{ is_expanded }">
      <SidebarNavItem
        :to="{ name: ROUTES_EMPLOYEES.CLIENT_DASHBOARD }"
        :label="$t('navbar.employee.dashboard')"
        icon="regular-house"
        :is_active="is_route_active(ROUTES_EMPLOYEES.CLIENT_DASHBOARD)"
        :is_expanded="is_expanded"
        data_test="employee-navbar-dashboard"
        :on_click="closeMobileDrawer"
      />

      <SidebarNavItem
        :to="{ name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE, params: { profile_type: PROFILE_TYPES.CUSTOMER } }"
        :label="$t('navbar.employee.contacts')"
        icon="regular-users"
        :is_active="is_route_active(ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE)"
        :is_expanded="is_expanded"
        data_test="employee-navbar-contacts"
        :on_click="closeMobileDrawer"
      />

      <SidebarNavItem
        :to="overview_to"
        :label="$t('navbar.employee.overview')"
        icon="regular-layer-group"
        :is_active="is_route_active(overview_route_names)"
        :is_expanded="is_expanded"
        data_test="employee-navbar-all-projects"
        :on_click="closeMobileDrawer"
      />

      <SidebarNavItem
        v-if="current_client.attributes.ooo"
        :to="{ name: ROUTES_EMPLOYEES.CLIENT_COMPANY, hash: '#ooo' }"
        :label="$t('client.labels.warningooo')"
        icon="regular-triangle-exclamation"
        :tooltip="current_client.attributes.ooo_note"
        tone="warning"
        :is_expanded="is_expanded"
        data_test="employee-sidebar-ooo"
      />
    </template>

    <template #project_list>
      <TheSidebarProjectList :on_close_mobile_drawer="closeMobileDrawer" />
    </template>
  </SidebarDrawer>
</template>

<script setup>
import { computed, getCurrentInstance, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useMqService } from '@/plugins/mq';
import SidebarDrawer from '@/app/shared_components/sidebar/SidebarDrawer.vue';
import SidebarNavItem from '@/app/shared_components/sidebar/SidebarNavItem.vue';
import TheSidebarProjectList from '@/app/_employees/components/sidebars/TheSidebarProjectList.vue';
import SelectTenantModal from '@/app/shared_components/modals/SelectTenantModal.vue';
import SidebarClientSwitcher from '@/app/shared_components/sidebar/SidebarClientSwitcher.vue';
import DropdownOption from '@/app/shared_components/buttons/DropdownOption.vue';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEE_OVERVIEWS, ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { LOCALSTORAGE_OPTIONS, localStorageKey } from '@/store/lib/local_storage';

const { proxy: app } = getCurrentInstance();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const mq = useMqService();
const store = useStore();
const route = useRoute();

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const current_profile_true = computed(() => store.getters.current_profile_true);
const current_is_impersonation = computed(() => store.getters.current_is_impersonation);

const storage_key_expand = computed(() => localStorageKey(current_profile.value, LOCALSTORAGE_OPTIONS.NAV_DRAWER));

const mobile_open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const closeMobileDrawer = () => {
  if (mq.isMobile) emit('update:modelValue', false);
};

const has_multiple_profiles = computed(() => {
  if (current_is_impersonation.value && current_profile_true.value.type === PROFILE_TYPES.EMPLOYEE) return false;
  return current_profile_true.value.attributes.profiles_count > 1;
});

const openSelectTenantModal = () => {
  app.$buefy.modal.open({ component: SelectTenantModal });
};

const overview_to = computed(() => ({ name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_ALL_PROJECTS }));
const overview_route_names = Object.values(ROUTES_EMPLOYEE_OVERVIEWS);

const is_route_active = (name_or_set) =>
  Array.isArray(name_or_set) ? name_or_set.includes(route.name) : route.name === name_or_set;

watch(
  () => route.path,
  () => {
    if (mq.isMobile) closeMobileDrawer();
  },
);
</script>
